import { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import RouteProps from '@/types/shared-types';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import {
  FullWidthStyledInput, StyledButtonWithStates, StyledCheckbox, StyledSelect,
} from '../../components/global/Global.style';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';
import SoftHider from '@/components/softHider/SoftHider';
import Loader from '@/components/loader/Loader';
import StepBack from '@/components/stepBack/StepBack';
import PageHeader from '@/components/pageHeader/PageHeader';
import { clearErrorMessage, setErrorMessage } from '@/features/paymentProcess/transactionSlice';

import { formFields, getFormResolver } from './gocardlessPaymentFields';
import { useGocardlessVerifyBank } from './useGocardlessVerifyBank';
import { setIsAccountHolder, updateGocardlessFields } from './gocardlessSlice';
import {
  FormFieldsWrapper, AccountHolderWrapper,
} from './GocardlessPayment.style';

function GocardlessPayment({ totalSteps, currencySymbol }: RouteProps) {
  const dispatch = useAppDispatch();
  const gocardlessState = useAppSelector(state => state.gocardless);
  const journeyNavigate = useJourneyNavigate();

  const { bankVerifyIsLoading, triggerVerifyBankApi } = useGocardlessVerifyBank();

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: getFormResolver(),
    defaultValues: {
      isAccountHolder: gocardlessState.isAccountHolder,
      accountHolderName: gocardlessState.accountHolderName,
      sortCode: gocardlessState.sortCode,
      accountNumber: gocardlessState.accountNumber,
      billingDay: gocardlessState.billingDay,
    },
  });

  const {
    register,
    watch,
    handleSubmit,
    formState: {
      errors: formErrors,
    },
  } = formMethods;

  useEffect(() => {
    const watchInputs = watch((formValues, { name }) => {
      if (!name) {
        return;
      }

      if (name === 'isAccountHolder') {
        const newIsAccountHolder = !!formValues[name];
        dispatch(setIsAccountHolder(newIsAccountHolder));
      } else {
        dispatch(updateGocardlessFields({
          [name]: formValues[name],
        }));
      }
    });
    return () => watchInputs.unsubscribe();
  }, [watch, dispatch]);

  const onSubmit = async () => {
    const callExecuteApi = () => {
      void triggerVerifyBankApi(
        {
          onSuccess: () => {
            dispatch(clearErrorMessage());
            journeyNavigate('next');
          },
          onError: () => {
            dispatch(setErrorMessage('Your bank account could not be validated, please check the details and try again.'));
          },
        },
      );
    };

    void callExecuteApi();
  };

  return (
    <SoftHider show={!bankVerifyIsLoading} loader={<Loader />}>
      <form onSubmit={event => void handleSubmit(onSubmit)(event)} noValidate>
        <PageHeader
          currentStep={3}
          totalSteps={totalSteps}
          headerText="Your Direct Debit"
          initialText="You're making a repeating monthly donation of"
          currencySymbol={currencySymbol}
        />

        <FormFieldsWrapper>
          <AccountHolderWrapper>
            <StyledCheckbox
              label={formFields.isAccountHolder.label}
              id={formFields.isAccountHolder.id}
              type={formFields.isAccountHolder.type}
              checked={gocardlessState.isAccountHolder}
              value=""
              {...register(formFields.isAccountHolder.id)}
            />
          </AccountHolderWrapper>

          {(!gocardlessState.isAccountHolder ? (

            <FullWidthStyledInput
              label={formFields.accountHolderName.label}
              id={formFields.accountHolderName.id}
              type={formFields.accountHolderName.type}
              errorMsg={formErrors?.accountHolderName?.message}
              {...register(formFields.accountHolderName.id)}
            />

          ) : null)}

          <FullWidthStyledInput
            label={formFields.sortCode.label}
            id={formFields.sortCode.id}
            type={formFields.sortCode.type}
            errorMsg={formErrors?.sortCode?.message}
            {...register(formFields.sortCode.id)}
          />

          <FullWidthStyledInput
            label={formFields.accountNumber.label}
            id={formFields.accountNumber.id}
            type={formFields.accountNumber.type}
            errorMsg={formErrors?.accountNumber?.message}
            {...register(formFields.accountNumber.id)}
          />

          <StyledSelect
            label={formFields.billingDay.label}
            id={formFields.billingDay.id}
            options={formFields.billingDay.options}
            errorMsg={formErrors?.billingDay?.message}
            {...register(formFields.billingDay.id)}
          />
        </FormFieldsWrapper>

        <PaymentErrorText $addMargin />

        <StyledButtonWithStates
          type="submit"
          fullWidth
          style={{ margin: 0 }}
          data-test="directDebitPaymentContinue"
        >
          Set up your Direct Debit
        </StyledButtonWithStates>
      </form>

      <StepBack data-test="directDebitPaymentGoBack" />

    </SoftHider>
  );
}

export default GocardlessPayment;
